import { useState } from 'react';
import { Handle, Position, type NodeProps } from '@xyflow/react';
import { Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { useIvrEditor } from '../../context';
import type { IvrNodeData, TransferAction } from '../../types';
import { NodeCard } from '../NodeCard';

export function TransferNode(props: NodeProps) {
  const { updateNodeData, onRemove, agentGroups, readOnly } = useIvrEditor();
  const data = props.data as IvrNodeData;
  const action = (data.action ?? {}) as TransferAction;
  const groupId = action.id ?? '';

  const [open, setOpen] = useState(false);

  const onPick = (idStr: string) => {
    const picked = agentGroups.find((g) => String(g.groupId) === idStr);
    if (!picked) return;
    updateNodeData(props.id, {
      action: {
        type: 'transfer_group',
        id: picked.groupId,
        name: picked.groupName,
      } as TransferAction,
    });
  };

  return (
    <>
      <Handle
        type="target"
        position={Position.Left}
        className="!h-3 !w-3 !border-2 !border-white !bg-amber-500"
      />
      <NodeCard
        verb="transfer"
        icon={Users}
        label="Transfer"
        status={action.id ? 'configured' : 'incomplete'}
        selected={props.selected}
        configurable={!readOnly}
        removable={!readOnly}
        summary={
          action.name ? (
            <span className="truncate">→ {action.name}</span>
          ) : (
            <span className="text-muted-foreground">Pick an agent group</span>
          )
        }
        onConfigure={() => setOpen(true)}
        onRemove={() => onRemove(props.id)}
      />
      <Handle
        type="source"
        position={Position.Right}
        className="!h-3 !w-3 !border-2 !border-white !bg-amber-500"
      />

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className="w-full sm:max-w-md">
          <SheetHeader>
            <SheetTitle>Transfer to agent group</SheetTitle>
            <SheetDescription>
              Patch the caller to one of your agent groups.
            </SheetDescription>
          </SheetHeader>
          <div className="mt-4 space-y-2">
            <Label>
              Agent group
              <span className="ml-1 text-destructive">*</span>
            </Label>
            <Select value={String(groupId)} onValueChange={onPick}>
              <SelectTrigger>
                <SelectValue
                  placeholder={
                    agentGroups.length === 0
                      ? 'No agent groups configured'
                      : 'Pick a group'
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {agentGroups.map((g) => (
                  <SelectItem key={g.groupId} value={String(g.groupId)}>
                    {g.groupName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <SheetFooter>
            <Button onClick={() => setOpen(false)}>Done</Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </>
  );
}